import {
	GFADocumentType,
	PAGES,
	GenerateMultipleGFADocumentsResponse,
	GenerateGFADocumentsDto,
} from "@dune/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { GFAAutorisationsAndReferencesFormData } from "../../component/gfa/useGFAAutorisationsAndReferencesForm";
import { GFAFinancingsFormData } from "../../component/gfa/useGFAFinancingsForm";
import { GFAShareholdersAndExperiencesFormData } from "../../component/gfa/useGFAShareholdersAndExperiencesForm";
import { BACKEND_URL } from "../../environment/environment";
import { privateAxios } from "../axios";
import { gfasQueryKeys } from "../query-keys/gfas.query-keys";
import { getErrorMessage } from "@dune/vars";
import { useNavigate } from "react-router-dom";

export const useLinkFileToGFA = (
	gfaId: number,
	options?: {
		showToast?: boolean;
		invalidateDocuments?: boolean;
	},
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: {
			fileId: string;
			type: GFADocumentType;
		}): Promise<void> => {
			await privateAxios.post(`/gfa/${gfaId}/documents`, data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.filesOnGFA(gfaId.toString()),
			});
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.detail(gfaId.toString()),
			});
			if (options?.invalidateDocuments) {
				queryClient.invalidateQueries({
					predicate: (query) => {
						const key = query.queryKey;
						return (
							key[0] === "gfas" && key[1] === "documents" && key[2] === gfaId
						);
					},
				});
			}
		},
		onError: (error) => {
			if (options?.showToast) {
				const errorMessage = getErrorMessage(error);
				toast.error("Erreur lors de l'upload du document", {
					description: errorMessage,
				});
			}
		},
	});
};

export const useUnlinkFileFromGFA = (
	gfaId: number,
	options?: {
		onMutate?: (fileId: string) => void;
		onSettled?: () => void;
		showToast?: boolean;
		invalidateDocuments?: boolean;
	},
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (fileOnGfaId: string): Promise<void> => {
			await privateAxios.delete(`/gfa/${gfaId}/unlink-file/${fileOnGfaId}`);
		},
		onMutate: options?.onMutate,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.filesOnGFA(gfaId.toString()),
			});
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.detail(gfaId.toString()),
			});
			if (options?.invalidateDocuments) {
				queryClient.invalidateQueries({
					predicate: (query) => {
						const key = query.queryKey;
						return (
							key[0] === "gfas" && key[1] === "documents" && key[2] === gfaId
						);
					},
				});
			}
			if (options?.showToast) {
				toast.success("Document supprimé avec succès");
			}
		},
		onError: (error) => {
			if (options?.showToast) {
				const errorMessage = getErrorMessage(error);
				toast.error("Erreur lors de la suppression du document", {
					description: errorMessage,
				});
			}
		},
		onSettled: options?.onSettled,
	});
};

export const saveGFAFinancings = async (params: {
	gfaId: number;
	data: GFAFinancingsFormData;
}) => {
	const { gfaId, data } = params;
	const { data: response } = await privateAxios.post(
		`${BACKEND_URL}/gfa/financings`,
		{
			gfaId,
			...data,
		},
	);
	return response;
};

export const useSaveGFAFinancings = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: saveGFAFinancings,
		onSuccess: async (_data, variables) => {
			toast("Financement de l'opération validé");
			await queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.detail(variables.gfaId.toString()),
				refetchType: "active",
			});
		},
		onError: (error: AxiosError<{ message?: string }>) => {
			const errorMessage =
				error?.response?.data?.message ||
				error?.message ||
				"Echec lors de la modification des financements";
			toast.error(errorMessage);
			console.error("GFA financings update error:", error);
		},
	});
};

export const saveGFAShareholdersAndExperiences = async (params: {
	gfaId: number;
	data: GFAShareholdersAndExperiencesFormData;
}) => {
	const { gfaId, data } = params;

	// Transform form data to backend format
	const transformedData = {
		...data,
		shareholders: data.shareholders.map((shareholder) => ({
			...shareholder,
			participationPercentage: Number(shareholder.participationPercentage),
		})),
		experiences: data.experiences.map((experience) => ({
			...experience,
			year: experience.year ? Number(experience.year) : undefined,
			salePriceTTC: experience.salePriceTTC
				? Number(experience.salePriceTTC)
				: undefined,
			progressPercentage: experience.progressPercentage
				? Number(experience.progressPercentage)
				: undefined,
			unsoldStockValue: experience.unsoldStockValue
				? Number(experience.unsoldStockValue)
				: undefined,
			marginAmount: experience.marginAmount
				? Number(experience.marginAmount)
				: undefined,
		})),
	};

	const { data: response } = await privateAxios.post(
		`${BACKEND_URL}/gfa/shareholders-and-experiences`,
		{
			gfaId,
			...transformedData,
			...(data.isRealEstateSocietyDifferentThanMOA === "false" && {
				realEstateSociety: undefined,
			}),
		},
	);
	return response;
};

export const useSaveGFAShareholdersAndExperiences = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: saveGFAShareholdersAndExperiences,
		onSuccess: async (_data, variables) => {
			toast("Actionnariat & Expérience validé");
			await queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.detail(variables.gfaId.toString()),
				refetchType: "active",
			});
		},
		onError: (error: AxiosError<{ message?: string }>) => {
			const errorMessage =
				error?.response?.data?.message ||
				error?.message ||
				"Echec lors de la modification de l'actionnariat & expérience";
			toast.error(errorMessage);
			console.error("GFA shareholders and experiences error:", error);
		},
	});
};

const saveGFAAutorisationsAndReferences = async (params: {
	gfaId: number;
	data: GFAAutorisationsAndReferencesFormData;
}) => {
	const { gfaId, data } = params;
	const { data: response } = await privateAxios.post(
		`${BACKEND_URL}/gfa/autorisations-and-references`,
		{
			gfaId,
			...data,
		},
	);
	return response;
};

export const useSaveGFAAutorisationsAndReferences = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: saveGFAAutorisationsAndReferences,
		onSuccess: async (_data, variables) => {
			toast("Autorisations & Références validé");
			await queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.detail(variables.gfaId.toString()),
				refetchType: "active",
			});
		},
		onError: (error: AxiosError<{ message?: string }>) => {
			const errorMessage =
				error?.response?.data?.message ||
				error?.message ||
				"Echec lors de la modification des autorisations & références";
			toast.error(errorMessage);
			console.error("GFA autorisations and references error:", error);
		},
	});
};

export const submitGFA = async (gfaId: number) => {
	const { data } = await privateAxios.patch(
		`${BACKEND_URL}/gfa/${gfaId}/submit`,
	);
	return data;
};

export const useSubmitGFA = () => {
	const navigate = useNavigate();
	return useMutation({
		mutationFn: submitGFA,
		onSuccess: (data) => {
			toast.success("Le projet GFA a été soumis avec succès");
			navigate(PAGES.GFA.show.withParams(data.id.toString()));
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la soumission du projet GFA", {
				description: errorMessage,
			});
		},
	});
};

export const createGFAOffer = async (params: {
	gfaId: number;
	applicableRate: number;
	duneFees: number;
	ancillaryBrokerageFees: number;
	primeTTC: number;
	primeClientTTC: number;
	commissionApporteur: number;
	remunerationApporteur: number;
	commissionDune: number;
	remunerationDune: number;
}) => {
	const { gfaId, ...data } = params;
	const { data: response } = await privateAxios.post<{
		offerId: number;
		pricingDetailsId: number;
		propositionId: number;
		propositionReference: string;
	}>(`${BACKEND_URL}/gfa/${gfaId}/offer`, data);
	return response;
};

export const useCreateGFAOffer = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: createGFAOffer,
		onSuccess: (data, variables) => {
			toast.success("Offre et proposition GFA créées avec succès", {
				description: `Référence proposition: ${data.propositionReference}`,
			});
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.detail(variables.gfaId.toString()),
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la création de l'offre GFA", {
				description: errorMessage,
			});
		},
	});
};

const generateMultipleGFADocuments = async (
	gfaId: number,
	data: GenerateGFADocumentsDto,
): Promise<GenerateMultipleGFADocumentsResponse> => {
	const { data: response } =
		await privateAxios.post<GenerateMultipleGFADocumentsResponse>(
			`/gfa/${gfaId}/generate-multiple-documents`,
			data,
		);
	return response;
};

export const useGenerateMultipleGFADocuments = (gfaId: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: GenerateGFADocumentsDto) =>
			generateMultipleGFADocuments(gfaId, data),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: gfasQueryKeys.documents(gfaId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: gfasQueryKeys.tasks(gfaId.toString()),
					refetchType: "active",
				}),
			]);
		},
	});
};
